import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { Download, HelpCircle, Loader2, Undo2, Upload } from 'lucide-react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  exportOasisBatch,
  exportOasisVisit,
  listOasisManagerVisits,
  markVisitsExported,
  unmarkVisitsExported,
} from '../services/api';
import type { OasisManagerVisitItem } from '../services/api/types.gen';
import type { OASISManagerFilters } from '../store/slices/oasisManagerSlice';
import { formatDateToMMDDYYYY, parseISOToLocalDate } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToastContext';
import { getVisitTypeDisplayName } from '../hooks/useVisitTypeOptions';
import {
  getResponseDetailFromBlobOrJson,
  getUserFacingApiMessage,
} from '../utils/apiErrorDetail';
import { OpenOasisReportWithQueryState } from './OpenOasisReport';

interface ParsedError {
  message: string;
  error_code?: string;
}

async function parseErrorDetail(error: unknown): Promise<ParsedError | null> {
  const detail = await getResponseDetailFromBlobOrJson(error);
  if (typeof detail === 'string') return { message: detail };
  if (detail && typeof detail === 'object') return detail as ParsedError;
  return null;
}

const STATUS_BY_TAB: Record<
  Exclude<OASISManagerFilters['activeTab'], 'incomplete'>,
  string
> = {
  ready_to_export: 'completed',
  exported: 'exported',
};

// Zero-padded CMS correction numbers. 00 = new submission; 01, 02, … are
// resubmissions of an already-accepted OASIS. The backend accepts 00–99; the
// dropdown is capped at 10, which covers realistic resubmission counts.
const CORRECTION_NUM_OPTIONS = Array.from({ length: 11 }, (_, i) =>
  String(i).padStart(2, '0')
);
const DEFAULT_CORRECTION_NUM = '00';

// CMS transaction type follows from the correction number: a new submission
// (00) is trans_type 1; any correction is a modification (trans_type 2).
function transTypeForCorrectionNum(correctionNum: string): '1' | '2' {
  return correctionNum === DEFAULT_CORRECTION_NUM ? '1' : '2';
}

function daysUntilDeadline(visitDate: string): number {
  const d = parseISOToLocalDate(visitDate);
  if (!d) return 0;
  const deadlineUtc = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate() + 30);
  const now = new Date();
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((deadlineUtc - todayUtc) / (1000 * 60 * 60 * 24));
}

const OASIS_MANAGER_TAB_VALUES = [
  'incomplete',
  'ready_to_export',
  'exported',
] as const;

const OASISManager: React.FC = () => {
  const { oasisManagerFilters, setOASISManagerFilters } = useStore(
    useShallow(state => ({
      oasisManagerFilters: state.oasisManagerFilters,
      setOASISManagerFilters: state.setOASISManagerFilters,
    }))
  );

  const [visits, setVisits] = useState<OasisManagerVisitItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Per-visit correction number chosen for the next export (transient — the
  // user sets it based on what CMS has already accepted; not persisted).
  const [correctionNums, setCorrectionNums] = useState<Record<string, string>>(
    {}
  );
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useQueryState(
    'tab',
    parseAsStringLiteral(OASIS_MANAGER_TAB_VALUES)
      .withDefault('incomplete')
      .withOptions({ history: 'replace', clearOnDefault: true })
  );
  const [isMarking, setIsMarking] = useState(false);
  const [clinicConfigError, setClinicConfigError] = useState<string | null>(
    null
  );
  const [searchInput, setSearchInput] = useState(
    oasisManagerFilters.patientSearch
  );
  const toast = useToast();
  const navigate = useNavigate();

  // Debounce patient search — wait 350ms after typing stops before fetching
  useEffect(() => {
    const timer = setTimeout(() => {
      setOASISManagerFilters({ patientSearch: searchInput });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const pageSize = 50;

  const fetchVisits = useCallback(async () => {
    if (activeTab === 'incomplete') return;

    setIsLoading(true);
    try {
      const status = STATUS_BY_TAB[activeTab];
      const response = await listOasisManagerVisits({
        query: {
          status: [status],
          patient_search: oasisManagerFilters.patientSearch || undefined,
          clinic_id: oasisManagerFilters.clinicId || undefined,
          page,
          page_size: pageSize,
        },
      });
      if (response.data) {
        setVisits(response.data.items);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to load OASIS visits:', err);
      toast.error('Failed to load OASIS visits. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [
    activeTab,
    oasisManagerFilters.patientSearch,
    oasisManagerFilters.clinicId,
    page,
  ]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  useEffect(() => {
    const visibleIds = new Set(visits.map(visit => visit.visit_id));
    setSelectedIds(prev => {
      const next = new Set(
        Array.from(prev).filter(visitId => visibleIds.has(visitId))
      );
      return next.size === prev.size ? prev : next;
    });
  }, [visits]);

  // Reset selection and page when tab changes
  useEffect(() => {
    setSelectedIds(new Set());
    setPage(1);
  }, [activeTab]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === visits.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visits.map(v => v.visit_id)));
    }
  };

  const getCorrectionNum = (visitId: string): string =>
    correctionNums[visitId] ?? DEFAULT_CORRECTION_NUM;

  const setCorrectionNum = (visitId: string, value: string) => {
    setCorrectionNums(prev => ({ ...prev, [visitId]: value }));
  };

  const handleExport = async () => {
    if (selectedIds.size === 0) return;
    setIsExporting(true);
    setClinicConfigError(null);
    try {
      const ids = Array.from(selectedIds);

      if (ids.length === 1) {
        // Single visit — export as XML
        const correctionNum = getCorrectionNum(ids[0]);
        const response = await exportOasisVisit({
          path: { visit_id: ids[0] },
          body: {
            answers: {},
            correction_num: correctionNum,
            trans_type_cd: transTypeForCorrectionNum(correctionNum),
          },
        });
        if (response.error) {
          const detail = await parseErrorDetail(response.error);
          if (detail && detail.error_code === 'CLINIC_CONFIG_REQUIRED') {
            setClinicConfigError(detail.message);
          } else {
            toast.error('Failed to export OASIS XML.');
          }
          return;
        }
        if (response.data) {
          const blob = new Blob([response.data as BlobPart], {
            type: 'application/xml',
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `oasis_${ids[0]}.xml`;
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 5000);
          toast.success('Exported OASIS assessment.');
        }
      } else {
        // Multiple visits — export as ZIP
        const response = await exportOasisBatch({
          body: {
            visits: ids.map(id => {
              const correctionNum = getCorrectionNum(id);
              return {
                visit_id: id,
                trans_type_cd: transTypeForCorrectionNum(correctionNum),
                correction_num: correctionNum,
              };
            }),
          },
        });
        if (response.error) {
          const detail = await parseErrorDetail(response.error);
          if (detail && detail.error_code === 'CLINIC_CONFIG_REQUIRED') {
            setClinicConfigError(detail.message);
          } else {
            toast.error('Failed to export OASIS ZIP.');
          }
          return;
        }
        if (response.data) {
          const blob = new Blob([response.data as BlobPart], {
            type: 'application/zip',
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `oasis_batch_${new Date().toISOString().slice(0, 10)}.zip`;
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 5000);
          toast.success(`Exported ${ids.length} OASIS assessments.`);
        }
      }
    } catch (err) {
      // Network or unexpected errors — API errors are handled via response.error above
      console.error('Error exporting OASIS:', err);
      toast.error('Failed to export OASIS. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleMarkExported = async () => {
    if (selectedIds.size === 0) return;
    setIsMarking(true);
    try {
      const response = await markVisitsExported({
        body: {
          visit_ids: Array.from(selectedIds),
        },
      });
      if (response.error) {
        toast.error(
          getUserFacingApiMessage(
            response,
            'Failed to mark visits as exported.'
          )
        );
        return;
      }
      if (response.data) {
        const { updated_count, skipped_ids } = response.data;
        if (skipped_ids.length > 0) {
          toast.success(
            `Marked ${updated_count} visit(s) as exported. ${skipped_ids.length} skipped.`
          );
        } else {
          toast.success(`Marked ${updated_count} visit(s) as exported.`);
        }
        setSelectedIds(new Set());
        fetchVisits();
      }
    } catch (err) {
      console.error('Error marking visits as exported:', err);
      toast.error('Failed to mark visits as exported. Please try again.');
    } finally {
      setIsMarking(false);
    }
  };

  const handleUnmarkExported = async () => {
    if (selectedIds.size === 0) return;
    setIsMarking(true);
    try {
      const response = await unmarkVisitsExported({
        body: { visit_ids: Array.from(selectedIds) },
      });
      if (response.error) {
        toast.error('Failed to move visits back to ready.');
        return;
      }
      toast.success('Moved visit(s) back to Ready to Export.');
      setSelectedIds(new Set());
      fetchVisits();
    } catch (err) {
      console.error('Error unmarking visits:', err);
      toast.error('Failed to move visits back to ready. Please try again.');
    } finally {
      setIsMarking(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const displayedVisits = visits;

  return (
    <div className='flex h-full flex-col'>
      <div className='border-b px-6 py-4'>
        <h1 className='text-lg font-semibold'>OASIS Manager</h1>
        <p className='text-sm text-muted-foreground'>
          Export OASIS assessments and track uploads to iQIES
        </p>
      </div>

      <div className='flex-1 overflow-auto px-6 py-4'>
        <Tabs
          value={activeTab}
          onValueChange={v => {
            const nextTab = v as OASISManagerFilters['activeTab'];
            setOASISManagerFilters({ activeTab: nextTab });
            void setActiveTab(nextTab);
          }}
        >
          <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4 mb-4'>
            <TabsList>
              <TabsTrigger value='incomplete'>Incomplete</TabsTrigger>
              <TabsTrigger value='ready_to_export'>Ready to Export</TabsTrigger>
              <TabsTrigger value='exported'>Exported</TabsTrigger>
            </TabsList>

            {activeTab !== 'incomplete' && (
              <div className='flex items-center gap-2 w-full md:w-auto'>
                <Input
                  placeholder='Search patients...'
                  inputSize='sm'
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className='w-full md:w-56'
                />
              </div>
            )}
          </div>

          <TabsContent value='incomplete' className='mt-0'>
            <OpenOasisReportWithQueryState showBackLink={false} />
          </TabsContent>

          {activeTab !== 'incomplete' && (
            <>
              {/* Clinic configuration error */}
              {clinicConfigError && (
                <div className='flex items-center gap-3 mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md'>
                  <div className='flex-1 text-sm'>
                    <p className='font-medium text-destructive'>
                      Clinic configuration required
                    </p>
                    <p className='text-muted-foreground mt-1'>
                      {clinicConfigError}. Update the clinic settings in{' '}
                      <button
                        className='underline text-primary hover:text-primary/80'
                        onClick={() => navigate('/admin')}
                      >
                        Admin &rarr; Clinics
                      </button>
                      .
                    </p>
                  </div>
                  <Button
                    size='sm'
                    variant='ghost'
                    onClick={() => setClinicConfigError(null)}
                  >
                    Dismiss
                  </Button>
                </div>
              )}

              {/* Action bar */}
              {selectedIds.size > 0 && (
                <div className='flex items-center gap-3 mb-4 p-3 bg-muted rounded-md'>
                  <span className='text-sm font-medium'>
                    {selectedIds.size} selected
                  </span>
                  <Button
                    size='sm'
                    onClick={handleExport}
                    disabled={isExporting}
                  >
                    {isExporting ? (
                      <Loader2 className='h-4 w-4 animate-spin mr-1' />
                    ) : (
                      <Download className='h-4 w-4 mr-1' />
                    )}
                    {selectedIds.size === 1 ? 'Export XML' : 'Export ZIP'}
                  </Button>
                  {activeTab === 'ready_to_export' && (
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={handleMarkExported}
                      disabled={isMarking}
                    >
                      {isMarking ? (
                        <Loader2 className='h-4 w-4 animate-spin mr-1' />
                      ) : (
                        <Upload className='h-4 w-4 mr-1' />
                      )}
                      Mark as Uploaded
                    </Button>
                  )}
                  {activeTab === 'exported' && (
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={handleUnmarkExported}
                      disabled={isMarking}
                    >
                      {isMarking ? (
                        <Loader2 className='h-4 w-4 animate-spin mr-1' />
                      ) : (
                        <Undo2 className='h-4 w-4 mr-1' />
                      )}
                      Move to Ready
                    </Button>
                  )}
                </div>
              )}

              <TabsContent value={activeTab} className='mt-0'>
                {isLoading ? (
                  <div className='flex items-center justify-center py-12'>
                    <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
                  </div>
                ) : visits.length === 0 ? (
                  <div className='flex items-center justify-center py-12 text-sm text-muted-foreground'>
                    {activeTab === 'ready_to_export'
                      ? 'No completed OASIS visits ready to export.'
                      : 'No exported OASIS visits.'}
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className='w-10'>
                            <Checkbox
                              checked={
                                visits.length > 0 &&
                                selectedIds.size === visits.length
                              }
                              onCheckedChange={toggleSelectAll}
                            />
                          </TableHead>
                          <TableHead>Patient</TableHead>
                          <TableHead>Visit Type</TableHead>
                          <TableHead>Visit Date</TableHead>
                          {activeTab === 'ready_to_export' && (
                            <TableHead>Days Remaining</TableHead>
                          )}
                          <TableHead>
                            <div className='flex items-center gap-1'>
                              Correction #
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type='button'
                                      className='inline-flex text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm'
                                      aria-label='About correction numbers'
                                    >
                                      <HelpCircle className='h-3.5 w-3.5' />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side='bottom'
                                    className='max-w-xs text-xs'
                                  >
                                    <p>
                                      Only change the correction number if you
                                      are retransmitting an already accepted
                                      OASIS and need to resubmit a corrected
                                      one. For the first resubmission use 01,
                                      second 02, and so on.
                                    </p>
                                    <p className='mt-1.5'>
                                      If an OASIS was rejected and you are
                                      retransmitting after making corrections,
                                      leave the correction number as 00.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </TableHead>
                          <TableHead>Clinician</TableHead>
                          <TableHead>Clinic</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayedVisits.map(visit => (
                          <TableRow key={visit.visit_id}>
                            <TableCell>
                              <Checkbox
                                checked={selectedIds.has(visit.visit_id)}
                                onCheckedChange={() =>
                                  toggleSelect(visit.visit_id)
                                }
                              />
                            </TableCell>
                            <TableCell className='font-medium'>
                              {visit.patient_name}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant='outline'
                                className='text-xs whitespace-nowrap'
                              >
                                {getVisitTypeDisplayName(visit.visit_type)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {formatDateToMMDDYYYY(visit.visit_date)}
                            </TableCell>
                            {activeTab === 'ready_to_export' && (
                              <TableCell>
                                {(() => {
                                  const days = daysUntilDeadline(
                                    visit.visit_date
                                  );
                                  return (
                                    <span
                                      className={
                                        days <= 0
                                          ? 'text-destructive font-medium'
                                          : days <= 7
                                            ? 'text-orange-500 font-medium'
                                            : 'text-muted-foreground'
                                      }
                                    >
                                      {days < 0
                                        ? 'Overdue'
                                        : days === 0
                                          ? 'Due today'
                                          : `${days}d`}
                                    </span>
                                  );
                                })()}
                              </TableCell>
                            )}
                            <TableCell>
                              <Select
                                value={getCorrectionNum(visit.visit_id)}
                                onValueChange={value =>
                                  setCorrectionNum(visit.visit_id, value)
                                }
                              >
                                <SelectTrigger
                                  selectSize='sm'
                                  className='w-20'
                                  aria-label='Correction number'
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {CORRECTION_NUM_OPTIONS.map(num => (
                                    <SelectItem key={num} value={num}>
                                      {num}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              {visit.assigned_clinician_name ?? '—'}
                            </TableCell>
                            <TableCell>{visit.clinic_name ?? '—'}</TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  visit.status === 'exported'
                                    ? 'default'
                                    : 'secondary'
                                }
                                className='text-xs'
                              >
                                {visit.status === 'exported'
                                  ? 'Exported'
                                  : 'Completed'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className='flex items-center justify-between mt-4'>
                        <span className='text-sm text-muted-foreground'>
                          Showing {(page - 1) * pageSize + 1}–
                          {Math.min(page * pageSize, total)} of {total}
                        </span>
                        <div className='flex gap-2'>
                          <Button
                            size='sm'
                            variant='outline'
                            disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                          >
                            Previous
                          </Button>
                          <Button
                            size='sm'
                            variant='outline'
                            disabled={page >= totalPages}
                            onClick={() => setPage(p => p + 1)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default OASISManager;
